/**
 * WebAuthn utilities for device persistence
 * Uses biometric authentication (Face ID/Touch ID/Fingerprint) to uniquely identify devices
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/types'

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
  return browserSupportsWebAuthn()
}

/**
 * Register a new device using WebAuthn
 * @param options - Registration options from backend
 * @returns Registration response to send to backend
 */
export async function registerDevice(
  options: PublicKeyCredentialCreationOptionsJSON
): Promise<RegistrationResponseJSON> {
  try {
    console.log('[WebAuthn] Starting device registration...')
    console.log('[WebAuthn] Registration options:', options)

    const response = await startRegistration({ optionsJSON: options })

    console.log('[WebAuthn] Registration successful')
    console.log('[WebAuthn] Credential ID:', response.id)

    return response
  } catch (error) {
    console.error('[WebAuthn] Registration failed:', error)

    if (error instanceof Error) {
      // User cancelled or error occurred
      if (error.name === 'NotAllowedError') {
        throw new Error('Device registration was cancelled or timed out')
      } else if (error.name === 'InvalidStateError') {
        throw new Error('This device is already registered')
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Your browser does not support biometric authentication')
      }
    }

    throw new Error('Failed to register device. Please try again.')
  }
}

/**
 * Authenticate an existing device using WebAuthn
 * @param options - Authentication options from backend
 * @returns Authentication response to send to backend
 */
export async function authenticateDevice(
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<AuthenticationResponseJSON> {
  try {
    console.log('[WebAuthn] Starting device authentication...')
    console.log('[WebAuthn] Authentication options:', options)

    const response = await startAuthentication({ optionsJSON: options })

    console.log('[WebAuthn] Authentication successful')
    console.log('[WebAuthn] Credential ID:', response.id)

    return response
  } catch (error) {
    console.error('[WebAuthn] Authentication failed:', error)

    if (error instanceof Error) {
      // User cancelled or error occurred
      if (error.name === 'NotAllowedError') {
        throw new Error('Authentication was cancelled or timed out')
      } else if (error.name === 'NotFoundError') {
        throw new Error('No registered device found')
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Your browser does not support biometric authentication')
      }
    }

    throw new Error('Failed to authenticate device. Please try again.')
  }
}

/**
 * Get device information for registration
 */
export function getDeviceInfo() {
  return {
    platform: navigator.platform || 'Unknown',
    userAgent: navigator.userAgent,
  }
}
